import { motion } from "framer-motion";
import { Check, ExternalLink, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n/LanguageContext";
import { TREATMENTS } from "../content/siteContent";
import { locT, locCat } from "../content/i18n_extra";
import { KanjiWatermark } from "./JapaneseMotifs";

export function TreatmentChapters() {
  const { t, lang } = useLang();

  return (
    <section id="tedaviler" className="relative bg-white py-28 sm:py-36 overflow-hidden" data-testid="treatments-section">
      <KanjiWatermark text="治療" className="right-2 top-8 text-[11rem] hidden lg:block" />
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="text-xs font-heading font-bold tracking-[0.3em] uppercase text-brand mb-4">{t("sec_treatments_kicker")}</div>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-ink text-balance">{t("sec_treatments_title")}</h2>
          <p className="mt-5 text-lg text-sub leading-relaxed">{t("sec_treatments_sub")}</p>
        </motion.div>

        <div className="mt-16 grid md:grid-cols-2 2xl:grid-cols-3 gap-6">
          {TREATMENTS.map((item, i) => {
            const c = locT(item, lang);
            const cat = locCat(item, lang);
            return (
              <motion.article
                key={item.id}
                data-testid={`treatment-card-${item.id}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08 }}
                className="group relative bg-[#F9FBFD] rounded-3xl border border-black/5 overflow-hidden hover:shadow-xl hover:shadow-brand/5 hover:-translate-y-1 transition-[transform,box-shadow] duration-300"
              >
                {item.img && (
                  <div className="relative h-44 overflow-hidden">
                    <img src={item.img} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#F9FBFD] via-transparent to-transparent" />
                    <span className="absolute top-4 left-4 text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full backdrop-blur-md" style={{ backgroundColor: "rgba(255,255,255,0.85)", color: item.accent }}>
                      {cat}
                    </span>
                  </div>
                )}
                <div className="p-8 pt-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-heading text-xl sm:text-2xl font-bold tracking-tight text-ink text-balance">{c.title}</h3>
                    {item.status && (
                      <span data-testid={`treatment-status-${item.id}`} className="mt-1 shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9.5px] font-bold uppercase tracking-wider"
                        style={item.status === "approved"
                          ? { backgroundColor: "rgba(120,168,137,0.14)", color: "#5d8a6d" }
                          : item.status === "research"
                            ? { backgroundColor: "rgba(201,169,106,0.16)", color: "#8A6B2F" }
                            : { backgroundColor: "rgba(198,74,59,0.10)", color: "#C64A3B" }}>
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.status === "approved" ? "#78A889" : item.status === "research" ? "#C9A96A" : "#C64A3B" }} />
                        {t(`mag_status_${item.status}`)}
                      </span>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs font-semibold" style={{ color: item.accent }}>{c.tag}</p>
                  <p className="mt-4 text-sm text-ink/60 leading-relaxed">{c.desc}</p>

                  <ul className="mt-5 grid sm:grid-cols-2 gap-x-4 gap-y-2">
                    {c.points.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-[13px] font-medium text-ink/75">
                        <span className="mt-0.5 w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.accent}18`, color: item.accent }}>
                          <Check size={11} strokeWidth={3} />
                        </span>
                        {p}
                      </li>
                    ))}
                  </ul>

                  {c.targets && (
                    <div className="mt-5 flex flex-wrap gap-1.5" data-testid={`treatment-targets-${item.id}`}>
                      {c.targets.map((tg, j) => (
                        <span key={j} className="px-2.5 py-1 rounded-full text-[11px] font-semibold border" style={{ borderColor: `${item.accent}40`, color: item.accent, backgroundColor: `${item.accent}0D` }}>
                          {tg}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <Link
                      to={`/tedavi/${item.id}`}
                      data-testid={`treatment-detail-link-${item.id}`}
                      className="inline-flex items-center gap-1.5 text-[13px] font-bold text-brand hover:gap-2.5 transition-[gap] duration-200"
                    >
                      {t("detail_link")} <ArrowRight size={13} />
                    </Link>
                    <a
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`treatment-link-${item.id}`}
                      className="inline-flex items-center gap-1.5 text-[13px] font-bold hover:gap-2.5 transition-[gap] duration-200"
                      style={{ color: item.accent }}
                    >
                      {t("visit_site")} <ExternalLink size={13} />
                    </a>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
